import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import {
  buildConfiguredPriceDriverCombinationRows,
  buildQualityAttributeValuesByHashMap,
  pivotQualityAttributes,
  toQualityAttributeValueHash,
} from "../../common/services/products/productUtilities";
import {
  getTermsByDate,
  getTermsByTermSchedule,
} from "../../common/services/markets/termService";
import { getFiniteValuesForKeys, joinArrays } from "../../common/services/utilities";
import MarkCell from "./markCell";
import { useTranslation } from "react-i18next";
import LineChart from "../../common/components/d3/lineChart";
import uuid from "react-uuid";
import {
  aggregateMarksWithoutDate,
} from "../../common/services/marketdata/marketDataUtilities";
import { ForwardReportingPeriodicity } from "../../common/enumerations/enums";

const getLineChartBounds = (marks = []) => {
  const values = getFiniteValuesForKeys(marks, ["price", "bid", "offer"]);

  const yMinRaw = values.length ? Math.min(...values) : Number.POSITIVE_INFINITY;
  const yMaxRaw = values.length ? Math.max(...values) : Number.NEGATIVE_INFINITY;

  const yMin =
    yMinRaw !== Number.POSITIVE_INFINITY
      ? yMinRaw > 0
        ? yMinRaw * 0.95
        : yMinRaw * 1.05
      : undefined;

  const yMax =
    yMaxRaw !== Number.NEGATIVE_INFINITY
      ? yMaxRaw > 0
        ? yMaxRaw * 1.05
        : yMaxRaw * 0.95
      : undefined;

  return { yMin, yMax };
};

const attachQualityAttributeValues = (mark, qualityAttributeValuesByHash) => {
  const existingQualityAttributeValues = mark?.qualityAttributeValues;
  const hasExistingQualityAttributeValues =
    Array.isArray(existingQualityAttributeValues) &&
    existingQualityAttributeValues.length > 0;

  if (hasExistingQualityAttributeValues) return mark;

  return {
    ...mark,
    qualityAttributeValues:
      qualityAttributeValuesByHash.get(mark?.qualityAttributeValueHash) ?? [],
  };
};

const buildProductCombinationRows = (product) =>
  pivotQualityAttributes(product).map((row) => ({
    ...row,
    qualityAttributeValueHash: toQualityAttributeValueHash(
      row.qualityAttributeValues
    ),
  }));

const filterTermsToMarkedTerms = (terms = [], marks = []) => {
  const markedTermIds = new Set(marks.map((mark) => mark.termId));

  return terms.filter((term) => markedTermIds.has(term.termId));
};

const MarksForwardTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const termsCacheRef = useRef(new Map());
  const termsRequestIdRef = useRef(0);
  const autoSizeFrameRef = useRef(null);
  const [tableState, setTableState] = useState({
    qualityAttributeValueHash: null,
    terms: [],
    isLoading: true,
  });

  const {
    marks: propsMarks,
    product,
    marketDate,
    termScheduleId,
    marketPriceDriverCombinations,
    organizationId,
    marketId,
    priceBasisId,
    currencyId,
    uomId,
    pricePrecision,
    volumePrecision,
    currencySymbol,
    forwardReportingPeriodicity,
  } = props;

  const rowHover = useCallback((data) => {
    setTableState((prevState) =>
      prevState.qualityAttributeValueHash === data.qualityAttributeValueHash
        ? prevState
        : {
            ...prevState,
            qualityAttributeValueHash: data.qualityAttributeValueHash,
          }
    );
  }, []);

  const rowExit = useCallback(() => {
    setTableState((prevState) =>
      prevState.qualityAttributeValueHash === null
        ? prevState
        : {
            ...prevState,
            qualityAttributeValueHash: null,
          }
    );
  }, []);

  const joinMarksToTerms = useCallback((marks, terms) => {
    const joinedMarks = joinArrays(marks, terms, "termId");
    const expandedMarks = [];

    joinedMarks.forEach((mark) => {
      if (forwardReportingPeriodicity === ForwardReportingPeriodicity.Year) {
        expandedMarks.push({
          ...mark,
          date: mark.startDate,
        });
        return;
      }

      let currentDate = new Date(mark.startDate);

      while (currentDate <= new Date(mark.endDate)) {
        expandedMarks.push({
          ...mark,
          date: currentDate.toISOString(),
        });

        currentDate.setMonth(currentDate.getMonth() + 1);
        currentDate.setDate(1);
      }
    });

    return expandedMarks;
  }, [forwardReportingPeriodicity]);

  const columnDefs = useMemo(() => {
    if (!product) return [];

    return [
      {
        field: "shortName",
        headerName: t("MarketDataProduct"),
        filter: false,
        sortable: false,
        pinned: "left",
        suppressMovable: true,
      },
      ...product.qualityAttributes
        .filter((qualityAttribute) => qualityAttribute.isPriceDriver !== false)
        .map((qualityAttribute) => ({
          field: qualityAttribute.qualityAttributeId,
          headerName: qualityAttribute.qualityAttributeName,
          filter: false,
          sortable: false,
          pinned: "left",
          suppressMovable: true,
        })),
      ...[...tableState.terms]
        .sort((a, b) => (new Date(a.endDate) > new Date(b.endDate) ? 1 : -1))
        .map((term) => ({
          field: term.termId,
          headerName: term.name,
          filter: false,
          sortable: false,
          cellDataType: false,
          cellRenderer: (params) => {
            const data = params.data;
            return (
              <MarkCell
                termId={term.termId}
                organizationId={organizationId}
                marketId={marketId}
                priceBasisId={priceBasisId}
                currencyId={currencyId}
                uomId={uomId}
                pricePrecision={pricePrecision}
                volumePrecision={volumePrecision}
                currencySymbol={currencySymbol}
                marketDate={marketDate}
                mark={
                  data[term.termId] ?? {
                    markId: uuid(),
                    eventId: uuid(),
                    active: true,
                  }
                }
                qualityAttributeValues={data.qualityAttributeValues}
                shortName={data.shortName}
                isNew={!data[term.termId]}
              />
            );
          },
        })),
    ];
  }, [
    product,
    tableState.terms,
    t,
    organizationId,
    marketId,
    priceBasisId,
    currencyId,
    uomId,
    pricePrecision,
    volumePrecision,
    currencySymbol,
    marketDate,
  ]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      type: "centerAligned",
      minWidth: 140,
      wrapText: false,
      autoHeight: false,
      wrapHeaderText: false,
      autoHeaderHeight: false,
    }),
    []
  );

  useEffect(() => {
    return () => {
      if (autoSizeFrameRef.current) {
        cancelAnimationFrame(autoSizeFrameRef.current);
      }
    };
  }, []);

  const autoSizeGridColumns = useCallback((params) => {
    if (!params?.api) return;

    if (autoSizeFrameRef.current) {
      cancelAnimationFrame(autoSizeFrameRef.current);
    }

    autoSizeFrameRef.current = requestAnimationFrame(() => {
      params.columnApi?.autoSizeAllColumns?.(false);
      params.api?.autoSizeAllColumns?.(false);
      autoSizeFrameRef.current = null;
    });
  }, []);

  const joinMarks = useCallback((pivotedData, marks) => {
    const groupedByQualityAttributeValueHash = marks.reduce((result, obj) => {
      const { qualityAttributeValueHash, termId, ...rest } = obj;

      if (!result[qualityAttributeValueHash]) {
        result[qualityAttributeValueHash] = {};
      }

      result[qualityAttributeValueHash][termId] = { ...rest };

      return result;
    }, {});

    const pivotedMarks = Object.entries(groupedByQualityAttributeValueHash).map(
      ([qualityAttributeValueHash, terms]) => ({
        qualityAttributeValueHash,
        ...terms,
      })
    );

    return joinArrays(pivotedData, pivotedMarks, "qualityAttributeValueHash");
  }, []);

  const { marks, lineChartMarksRaw, startDate, endDate } = useMemo(() => {
    if (!product) {
      return {
        marks: [],
        lineChartMarksRaw: [],
        startDate: new Date(),
        endDate: new Date(),
      };
    }

    const configuredCombinationRows = buildConfiguredPriceDriverCombinationRows(
      product,
      marketPriceDriverCombinations
    );
    const combinationRows =
      configuredCombinationRows.length > 0
        ? configuredCombinationRows
        : buildProductCombinationRows(product);
    const qualityAttributeValuesByHash =
      buildQualityAttributeValuesByHashMap(combinationRows);
    const validHashes = new Set(
      combinationRows.map((row) => row.qualityAttributeValueHash)
    );
    const nextLineChartMarksRaw = joinMarksToTerms(propsMarks, tableState.terms)
      .filter((mark) => validHashes.has(mark.qualityAttributeValueHash))
      .map((mark) =>
        attachQualityAttributeValues(mark, qualityAttributeValuesByHash)
      );
    const aggregatedMarks = aggregateMarksWithoutDate(propsMarks);
    const joinedData = joinMarks(combinationRows, aggregatedMarks);

    const nextStartDate =
      tableState.terms.length > 0
        ? new Date(
            tableState.terms.reduce((minDate, currentItem) => {
              return currentItem.startDate < minDate
                ? currentItem.startDate
                : minDate;
            }, tableState.terms[0].startDate)
          )
        : new Date();

    const nextEndDate =
      tableState.terms.length > 0
        ? new Date(
            tableState.terms.reduce((maxDate, currentItem) => {
              return currentItem.endDate > maxDate
                ? currentItem.endDate
                : maxDate;
            }, tableState.terms[0].endDate)
          )
        : new Date();

    return {
      marks: joinedData,
      lineChartMarksRaw: nextLineChartMarksRaw,
      startDate: nextStartDate,
      endDate: nextEndDate,
    };
  }, [
    propsMarks,
    product,
    marketPriceDriverCombinations,
    tableState.terms,
    joinMarksToTerms,
    joinMarks,
  ]);

  const lineChartMarks = useMemo(() => {
    if (!tableState.qualityAttributeValueHash) {
      return lineChartMarksRaw;
    }

    return lineChartMarksRaw.filter(
      (mark) =>
        mark.qualityAttributeValueHash === tableState.qualityAttributeValueHash
    );
  }, [lineChartMarksRaw, tableState.qualityAttributeValueHash]);

  const lineChartBounds = useMemo(
    () => getLineChartBounds(lineChartMarksRaw),
    [lineChartMarksRaw]
  );

  useEffect(() => {
    if (!product || !termScheduleId || !marketDate) return;

    const isYearlyForward =
      forwardReportingPeriodicity === ForwardReportingPeriodicity.Year;
    const cacheKey = `${termScheduleId}|${marketDate}|${forwardReportingPeriodicity}`;
    const cachedTerms = termsCacheRef.current.get(cacheKey);

    termsRequestIdRef.current += 1;
    const requestId = termsRequestIdRef.current;

    setTableState((prevState) => ({
      ...prevState,
      qualityAttributeValueHash: null,
      terms: cachedTerms ?? [],
      isLoading: !cachedTerms,
    }));

    if (cachedTerms) {
      return;
    }

    const initializeTable = async () => {
      try {
        const loadedTerms = isYearlyForward
          ? await getTermsByTermSchedule(termScheduleId)
          : await getTermsByDate(termScheduleId, marketDate);
        const terms = isYearlyForward
          ? filterTermsToMarkedTerms(loadedTerms, propsMarks)
          : loadedTerms;

        if (termsRequestIdRef.current !== requestId) {
          return;
        }

        termsCacheRef.current.set(cacheKey, terms);
        setTableState((prevState) => ({
          ...prevState,
          terms,
          isLoading: false,
        }));
      } catch (error) {
        console.error("Error initializing table:", error);

        if (termsRequestIdRef.current !== requestId) {
          return;
        }

        setTableState((prevState) => ({
          ...prevState,
          terms: [],
          isLoading: false,
        }));
      }
    };

    initializeTable();
  }, [
    product,
    propsMarks,
    marketDate,
    termScheduleId,
    forwardReportingPeriodicity,
  ]);

  if (tableState.isLoading) return <Loading />;

  return (
    <div className="container-fluid market-chart-panel">
      <div className="row market-data-graph">
        <LineChart
          data={lineChartMarks}
          qualityAttributes={product?.qualityAttributes}
          forwardReportingPeriodicity={forwardReportingPeriodicity}
          startDate={startDate}
          endDate={endDate}
          yMin={lineChartBounds.yMin}
          yMax={lineChartBounds.yMax}
          currencySymbol={currencySymbol}
          pricePrecision={pricePrecision}
        />
      </div>
      <div className="row marks-table">
        <ThemeableAgGrid
          className="table-condensed"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoSizeStrategy={{
            type: "fitCellContents",
            skipHeader: false,
            defaultMinWidth: 140,
          }}
          rowData={marks}
          onCellMouseOver={(event) => rowHover(event.data)}
          onCellMouseOut={() => rowExit()}
          onFirstDataRendered={autoSizeGridColumns}
          onRowDataUpdated={autoSizeGridColumns}
          onGridSizeChanged={autoSizeGridColumns}
          getRowId={(params) => params.data.qualityAttributeValueHash}
          domLayout="autoHeight"
          allowHorizontalScroll={true}
          animateRows={true}
        />
      </div>
    </div>
  );
};

export default MarksForwardTable;
